// SF22.2: the bone-mapping UI - a two-column list (canonical joints vs. an
// uploaded rig's own source joints), name-similarity auto-suggest with
// manual override, and a live preview that actually retargets and plays a
// clip on the source mesh through a temporary instance. `RetargetedGalleryStage`
// is exported too: it is the same clip-driven stage the live preview uses,
// reused as-is once a mapping is saved and the retargeted character is
// selected in AnimationRoute's existing 45-clip transport UI.
//
// Deliberately styled with inline rules over `var(--ss-*, fallback)`, the
// same convention CharacterStudio.tsx uses - the engine's library build
// emits no CSS, so this takes its tokens from the host when they exist and
// falls back to a dark palette when they don't.
import { OrbitControls, PerspectiveCamera } from '@react-three/drei';
import { useFrame } from '@react-three/fiber';
import { useCallback, useEffect, useRef, useState, type CSSProperties } from 'react';
import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';

import { PROTOTYPE_CLIPS, withClipPrefix, type PrototypeClip } from '../animation/clips';
import { disposeProp } from '../animation/GalleryStage';
import { loadPrototypeGltf } from '../animation/loader';
import { findSkinnedMesh, loadImportedRig, walkSkeleton, type ImportedBone } from '../rig/importRig';
import { buildRestPose, retargetClip, suggestBoneMapping, type BoneMapping, type RestPose } from '../rig/retarget';
import { Viewport } from '../../runtime/viewport/Viewport';

// ── Live preview / production stage ─────────────────────────────────────

export interface RetargetedGalleryProps {
  /** Source rig's GLB download URL. */
  url: string;
  /** Canonical joint name -> source joint name. */
  mapping: BoneMapping;
  clip: PrototypeClip;
  speed?: number;
  paused?: boolean;
  background?: string;
  style?: CSSProperties;
  className?: string;
}

interface CanonicalRig {
  rest: RestPose;
  animations: THREE.AnimationClip[];
}

function useCanonicalRig(): CanonicalRig | null {
  const [rig, setRig] = useState<CanonicalRig | null>(null);
  useEffect(() => {
    let cancelled = false;
    void loadPrototypeGltf().then((gltf) => {
      if (cancelled) return;
      const mesh = findSkinnedMesh(gltf.scene);
      const bones = mesh ? walkSkeleton(mesh) : [];
      setRig({ rest: buildRestPose(bones), animations: gltf.animations });
    });
    return () => {
      cancelled = true;
    };
  }, []);
  return rig;
}

interface LoadedSourceRig {
  scene: THREE.Object3D;
  mixer: THREE.AnimationMixer;
  rest: RestPose;
}

function RetargetedGalleryRig({ url, mapping, clip, speed = 1, paused = false }: RetargetedGalleryProps) {
  const canonical = useCanonicalRig();
  const [source, setSource] = useState<LoadedSourceRig | null>(null);
  const actionRef = useRef<THREE.AnimationAction | null>(null);

  useEffect(() => {
    let cancelled = false;
    let mixer: THREE.AnimationMixer | null = null;
    let loadedScene: THREE.Object3D | null = null;
    new GLTFLoader().load(
      url,
      (gltf) => {
        if (cancelled) return;
        const mesh = findSkinnedMesh(gltf.scene);
        if (!mesh) return;
        loadedScene = gltf.scene;
        gltf.scene.traverse((node) => {
          const m = node as THREE.Mesh;
          if (!m.isMesh) return;
          m.castShadow = true;
          m.receiveShadow = true;
        });
        mixer = new THREE.AnimationMixer(gltf.scene);
        setSource({ scene: gltf.scene, mixer, rest: buildRestPose(walkSkeleton(mesh)) });
      },
      undefined,
      (err) => console.warn('[rig] retarget preview failed to load source rig:', err),
    );
    return () => {
      cancelled = true;
      mixer?.stopAllAction();
      actionRef.current = null;
      setSource(null);
      if (loadedScene) disposeProp(loadedScene);
    };
  }, [url]);

  useEffect(() => {
    if (!source || !canonical) return;
    const canonicalClip = THREE.AnimationClip.findByName(canonical.animations, withClipPrefix(clip));
    if (!canonicalClip) return;
    const { clip: retargeted } = retargetClip(canonicalClip, mapping, canonical.rest, source.rest);
    const next = source.mixer.clipAction(retargeted);
    next.reset();
    next.setLoop(THREE.LoopRepeat, Infinity);
    next.timeScale = speed;
    next.fadeIn(0.2);
    next.play();
    const previous = actionRef.current;
    let uncachePrevious: ReturnType<typeof setTimeout> | undefined;
    if (previous && previous !== next) {
      previous.fadeOut(0.2);
      // retargetClip builds a brand-new AnimationClip on every mapping edit,
      // and mixer.clipAction() caches it internally - without releasing the
      // clip being faded out, that cache grows by one entry per edit for the
      // life of the mapping session. Release once the crossfade finishes.
      const previousClip = previous.getClip();
      uncachePrevious = setTimeout(() => {
        source.mixer.uncacheAction(previousClip, source.scene);
        source.mixer.uncacheClip(previousClip);
      }, 250);
    }
    actionRef.current = next;
    return () => {
      if (uncachePrevious) clearTimeout(uncachePrevious);
    };
  }, [source, canonical, mapping, clip, speed]);

  useFrame((_, dt) => {
    if (!paused) source?.mixer.update(dt);
  });

  return source ? <primitive object={source.scene} /> : null;
}

/**
 * The gallery scene chrome (matching AnimationGalleryStage/LibraryGalleryStage)
 * around a retargeted character: loads the source mesh once, retargets the
 * requested canonical clip onto it, and plays it with a real AnimationMixer.
 */
export function RetargetedGalleryStage(props: RetargetedGalleryProps) {
  const { background = '#11141b', style, className } = props;
  return (
    <div className={className} style={{ position: 'absolute', inset: 0, ...style }}>
      <Viewport shadows>
        <PerspectiveCamera makeDefault fov={45} near={0.1} far={100} position={[2.8, 1.7, 4.6]} />
        <color attach="background" args={[background]} />
        <hemisphereLight args={['#bcd2ff', '#202833', 1.1]} />
        <directionalLight
          color="#fff4e6"
          intensity={2.4}
          position={[3, 6, 4]}
          castShadow
          shadow-mapSize-width={1024}
          shadow-mapSize-height={1024}
        />
        <directionalLight color="#88aaff" intensity={0.8} position={[-4, 3, -3]} />
        <mesh rotation={[-Math.PI / 2, 0, 0]} receiveShadow>
          <circleGeometry args={[6, 48]} />
          <meshStandardMaterial color="#1a1f29" roughness={0.95} metalness={0} />
        </mesh>
        <gridHelper args={[12, 24, '#2a3344', '#222a36']} />
        <RetargetedGalleryRig {...props} />
        <OrbitControls target={[0, 1, 0]} enableDamping />
      </Viewport>
    </div>
  );
}

// ── Bone-mapping UI ──────────────────────────────────────────────────────

const STYLES = {
  status: {
    padding: 24,
    fontSize: 13,
    color: 'var(--ss-text-2, #8b929c)',
  } satisfies CSSProperties,
  root: {
    display: 'flex',
    minHeight: 0,
    flex: 1,
    background: 'var(--ss-surface, #14171c)',
    color: 'var(--ss-text, #dfe3e8)',
  } satisfies CSSProperties,
  listColumn: {
    width: 340,
    flexShrink: 0,
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0,
    borderRight: '1px solid var(--ss-border-soft, rgba(255,255,255,0.08))',
  } satisfies CSSProperties,
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '14px 16px',
    fontSize: 11,
    fontWeight: 600,
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
    color: 'var(--ss-text-2, #8b929c)',
    borderBottom: '1px solid var(--ss-border-soft, rgba(255,255,255,0.08))',
  } satisfies CSSProperties,
  count: {
    fontWeight: 500,
    letterSpacing: 'normal',
    textTransform: 'none',
    color: 'var(--ss-text-3, #6d747e)',
  } satisfies CSSProperties,
  rows: {
    flex: 1,
    minHeight: 0,
    overflowY: 'auto',
    padding: '4px 12px',
  } satisfies CSSProperties,
  row: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '5px 4px',
  } satisfies CSSProperties,
  canonicalName: {
    flex: 1,
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    fontSize: 12,
    fontFamily: 'var(--ss-mono, monospace)',
    color: 'var(--ss-text, #dfe3e8)',
  } satisfies CSSProperties,
  select: {
    flexShrink: 0,
    width: 168,
    padding: '5px 8px',
    fontSize: 11.5,
    borderRadius: 7,
    background: 'var(--ss-panel-strong, #1b1f26)',
    border: '1px solid var(--ss-border-soft, rgba(255,255,255,0.08))',
    color: 'var(--ss-text, #dfe3e8)',
  } satisfies CSSProperties,
  actions: {
    display: 'flex',
    gap: 8,
    padding: 14,
    borderTop: '1px solid var(--ss-border-soft, rgba(255,255,255,0.08))',
  } satisfies CSSProperties,
  primaryButton: {
    flex: 1,
    padding: '9px 14px',
    fontSize: 12.5,
    fontWeight: 600,
    borderRadius: 8,
    border: 'none',
    background: 'var(--ss-accent, #6444d6)',
    color: 'var(--ss-accent-ink, #ffffff)',
    cursor: 'pointer',
  } satisfies CSSProperties,
  ghostButton: {
    padding: '9px 14px',
    fontSize: 12.5,
    fontWeight: 500,
    borderRadius: 8,
    border: '1px solid var(--ss-border-soft, rgba(255,255,255,0.08))',
    background: 'transparent',
    color: 'var(--ss-text-2, #8b929c)',
    cursor: 'pointer',
  } satisfies CSSProperties,
  previewColumn: {
    flex: 1,
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
  } satisfies CSSProperties,
  previewStage: {
    position: 'relative',
    flex: 1,
    minHeight: 0,
  } satisfies CSSProperties,
  previewControls: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: 12,
    borderTop: '1px solid var(--ss-border-soft, rgba(255,255,255,0.08))',
  } satisfies CSSProperties,
} as const;

export interface BoneMappingPanelProps {
  /** Uploaded rigged GLB's download URL to build a mapping for. */
  sourceUrl: string;
  /** Called once the user accepts the current mapping, with the full
   * canonical -> source mapping and the source rig's own joint name list
   * (for the caller to persist as the new character's rigBones entry). */
  onSave: (mapping: BoneMapping, sourceJointNames: string[]) => void;
  onCancel: () => void;
}

type LoadState =
  | { status: 'loading' }
  | { status: 'error'; message: string }
  | { status: 'ready'; sourceBones: ImportedBone[]; canonicalNames: string[] };

/**
 * Two-column bone mapping: every canonical joint on the left, a `<select>`
 * of the uploaded rig's own joints on the right, pre-filled by
 * `suggestBoneMapping` and freely overridable. The right panel is a live
 * preview - the current mapping retargets and plays a real clip on the
 * actual uploaded mesh through a temporary `RetargetedGalleryStage`
 * instance, so a bad mapping is visible before it is ever saved.
 */
export function BoneMappingPanel({ sourceUrl, onSave, onCancel }: BoneMappingPanelProps) {
  const [state, setState] = useState<LoadState>({ status: 'loading' });
  const [mapping, setMapping] = useState<BoneMapping>({});
  const [previewClip, setPreviewClip] = useState<PrototypeClip>('Idle_Loop');
  const [previewPlaying, setPreviewPlaying] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setState({ status: 'loading' });
    Promise.all([loadImportedRig(sourceUrl), loadPrototypeGltf()])
      .then(([sourceBones, canonicalGltf]) => {
        if (cancelled) return;
        const canonicalMesh = findSkinnedMesh(canonicalGltf.scene);
        const canonicalNames = canonicalMesh ? walkSkeleton(canonicalMesh).map((b) => b.name) : [];
        setMapping(suggestBoneMapping(canonicalNames, sourceBones.map((b) => b.name)));
        setState({ status: 'ready', sourceBones, canonicalNames });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setState({ status: 'error', message: err instanceof Error ? err.message : 'Failed to load the rig.' });
      });
    return () => {
      cancelled = true;
    };
  }, [sourceUrl]);

  const setJoint = useCallback((canonicalName: string, sourceName: string) => {
    setMapping((prev) => {
      if (!sourceName) {
        const next = { ...prev };
        delete next[canonicalName];
        return next;
      }
      return { ...prev, [canonicalName]: sourceName };
    });
  }, []);

  if (state.status === 'loading') return <div style={STYLES.status}>Loading rig…</div>;
  if (state.status === 'error') {
    return <div style={{ ...STYLES.status, color: 'var(--ss-danger, #d0453f)' }}>{state.message}</div>;
  }

  const mappedCount = state.canonicalNames.filter((name) => mapping[name]).length;
  // Two canonical joints mapped to the same source bone drive that one bone
  // from two different retarget targets at once - the result is jittery
  // motion with no error, so surface it instead of letting it pass silently.
  const targetUseCount = new Map<string, number>();
  for (const sourceName of Object.values(mapping)) {
    if (!sourceName) continue;
    targetUseCount.set(sourceName, (targetUseCount.get(sourceName) ?? 0) + 1);
  }
  const duplicateTargetCount = [...targetUseCount.values()].filter((n) => n > 1).length;

  return (
    <div style={STYLES.root}>
      <div style={STYLES.listColumn}>
        <div style={STYLES.header}>
          <span>Bone mapping</span>
          <span style={STYLES.count}>
            {mappedCount} / {state.canonicalNames.length} mapped
            {duplicateTargetCount > 0 && (
              <span style={{ color: 'var(--ss-danger, #d0453f)', marginLeft: 8 }}>
                · {duplicateTargetCount} source bone{duplicateTargetCount > 1 ? 's' : ''} mapped twice
              </span>
            )}
          </span>
        </div>
        <div style={STYLES.rows}>
          {state.canonicalNames.map((canonicalName) => {
            const assigned = mapping[canonicalName];
            const isDuplicate = Boolean(assigned && (targetUseCount.get(assigned) ?? 0) > 1);
            return (
              <div key={canonicalName} style={STYLES.row}>
                <span style={STYLES.canonicalName}>{canonicalName}</span>
                <select
                  style={isDuplicate ? { ...STYLES.select, borderColor: 'var(--ss-danger, #d0453f)' } : STYLES.select}
                  value={assigned ?? ''}
                  title={isDuplicate ? `"${assigned}" is also mapped to another canonical joint - only one will drive it correctly` : undefined}
                  onChange={(e) => setJoint(canonicalName, e.target.value)}
                >
                  <option value="">(none)</option>
                  {state.sourceBones.map((bone) => (
                    <option key={bone.name} value={bone.name}>
                      {bone.name}
                    </option>
                  ))}
                </select>
              </div>
            );
          })}
        </div>
        <div style={STYLES.actions}>
          <button type="button" style={STYLES.ghostButton} onClick={onCancel}>
            Cancel
          </button>
          <button
            type="button"
            style={STYLES.primaryButton}
            onClick={() => onSave(mapping, state.sourceBones.map((bone) => bone.name))}
          >
            Save mapping
          </button>
        </div>
      </div>
      <div style={STYLES.previewColumn}>
        <div style={STYLES.previewStage}>
          <RetargetedGalleryStage url={sourceUrl} mapping={mapping} clip={previewClip} paused={!previewPlaying} />
        </div>
        <div style={STYLES.previewControls}>
          <button type="button" style={STYLES.ghostButton} onClick={() => setPreviewPlaying((playing) => !playing)}>
            {previewPlaying ? 'Pause' : 'Play'}
          </button>
          <select
            style={{ ...STYLES.select, flex: 1, width: 'auto' }}
            value={previewClip}
            onChange={(e) => setPreviewClip(e.target.value as PrototypeClip)}
          >
            {PROTOTYPE_CLIPS.map((clip) => (
              <option key={clip} value={clip}>
                {clip}
              </option>
            ))}
          </select>
        </div>
      </div>
    </div>
  );
}
